import { useCallback } from 'react'
import { useWardrobe } from '../../../../../hooks/useWardrobe'

/**
 * Centralized hook for outfit library data access.
 * Reads from formData.outfits and only fetches when data is empty.
 *
 * @param {Object} options
 * @param {Object} options.formData - Form data containing outfits
 * @param {Function} options.setFormData - Function to update form data
 * @returns {Object} Library state and actions
 */
export const useOutfitLibrary = ({ formData = {}, setFormData = () => {} }) => {
  const {
    isLoading,
    fetchLibrary,
    searchOutfits,
    filterOutfits,
    stopPolling
  } = useWardrobe()

  const outfits = formData?.outfits || []
  const hasData = outfits.length > 0

  /**
   * Fetch library only if no data exists
   */
  const fetchIfNeeded = useCallback(async () => {
    if (!hasData && !isLoading) {
      try {
        const items = await fetchLibrary({ page: 1 })
        if (items && setFormData) {
          setFormData(prev => ({ ...prev, outfits: items }))
        }
        return items
      } catch (err) {
        console.error('Failed to fetch library:', err)
        return null
      }
    }
    return outfits
  }, [hasData, isLoading, fetchLibrary, setFormData, outfits])

  /**
   * Force refresh library data (for manual refresh button)
   */
  const refresh = useCallback(
    async (params = {}) => {
      try {
        const items = await fetchLibrary({ page: 1, ...params })
        if (items && setFormData) {
          setFormData(prev => ({ ...prev, outfits: items }))
        }
        return items
      } catch (err) {
        console.error('Failed to refresh library:', err)
        return null
      }
    },
    [fetchLibrary, setFormData]
  )

  /**
   * Search outfits with query
   */
  const search = useCallback(
    async query => {
      try {
        const items = await fetchLibrary({ action: 'search', query, page: 1 })
        if (items && setFormData) {
          setFormData(prev => ({ ...prev, outfits: items }))
        }
        return items
      } catch (err) {
        console.error('Failed to search library:', err)
        return null
      }
    },
    [fetchLibrary, setFormData]
  )

  /**
   * Filter outfits by style/season
   */
  const filter = useCallback(
    async filters => {
      try {
        const items = await fetchLibrary({
          action: 'filter',
          ...filters,
          page: 1
        })
        if (items && setFormData) {
          setFormData(prev => ({ ...prev, outfits: items }))
        }
        return items
      } catch (err) {
        console.error('Failed to filter library:', err)
        return null
      }
    },
    [fetchLibrary, setFormData]
  )

  /**
   * Find outfit by tag or id
   */
  const findByTag = useCallback(
    tag => {
      return outfits.find(o => (o.tag || o.id) === tag) || null
    },
    [outfits]
  )

  return {
    outfits,
    hasData,
    isLoading,
    fetchIfNeeded,
    refresh,
    search,
    filter,
    findByTag,
    stopPolling
  }
}

export default useOutfitLibrary
